// Echtzeit-Brücke: socket.io-Hinweise des Servers → gezieltes Neuladen der betroffenen Abfragen.
// Hinweise tragen keine Daten (docs/API.md §5); geladen wird über die rechtegeprüften Aktionen.
//
// Der Echtzeit-Dienst entsteht parallel (Arbeitspaket „Kern Dienste“). Angenommen ist: socket.io unter
// /socket.io, Anmeldung über das Sitzungs-Cookie beim Verbindungsaufbau, Ereignis 'hint' mit
// { event, refs, locationIds }. Gibt es den Dienst nicht, bleibt die Oberfläche still ohne ihn bedienbar
// (Zustand 'unavailable', keine Leiste, kein wiederholtes Anklopfen).
import type { Query, QueryClient } from '@tanstack/react-query'
import { setRealtimeState, type RealtimeState } from '../stores/connection'
import { ACTION_KEY, hintMatches, type RefreshRule } from './keys'
import type { RealtimeHint } from './types'

export const REALTIME_PATH = '/socket.io'
export const REALTIME_EVENT = 'hint'

/** Der Ausschnitt von socket.io-client, den die Brücke braucht (in Tests ein Attrappen-Objekt). */
export interface RealtimeSocket {
  on(event: string, listener: (...args: never[]) => void): unknown
  connect(): unknown
  disconnect(): unknown
}

export function parseHint(value: unknown): RealtimeHint | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as { event?: unknown; refs?: unknown; locationIds?: unknown }
  if (typeof raw.event !== 'string' || raw.event === '') return null
  return {
    event: raw.event,
    refs: Array.isArray(raw.refs)
      ? raw.refs.filter(
          (ref): ref is RealtimeHint['refs'][number] =>
            typeof ref === 'object' && ref !== null && typeof (ref as { type?: unknown }).type === 'string',
        )
      : [],
    locationIds: Array.isArray(raw.locationIds)
      ? raw.locationIds.filter((id): id is string => typeof id === 'string')
      : [],
  }
}

/** Soll die Abfrage wegen dieses Hinweises neu laden? (Schlüssel und `meta.refreshOn` der Abfrage) */
export function queryMatchesHint(query: Pick<Query, 'queryKey' | 'meta'>, hint: RealtimeHint): boolean {
  const [scope, name] = query.queryKey
  if (scope !== ACTION_KEY || typeof name !== 'string') return false
  return hintMatches(query.meta?.refreshOn as RefreshRule | undefined, name, hint)
}

/** Hinweis anwenden: betroffene Abfragen gelten als veraltet, sichtbare laden sofort neu. */
export function applyHint(queryClient: QueryClient, hint: RealtimeHint): Promise<void> {
  return queryClient.invalidateQueries({ predicate: (query) => queryMatchesHint(query, hint) })
}

type HintListener = (hint: RealtimeHint) => void
const listeners = new Set<HintListener>()

/** Hinweise mitlesen (z. B. um eine geänderte Zeile kurz aufblinken zu lassen). Gibt die Abmeldung zurück. */
export function onRealtimeHint(listener: HintListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export interface RealtimeBridgeOptions {
  queryClient: QueryClient
  /** Standard: socket.io-client gegen die eigene Herkunft (wird erst beim Start nachgeladen) */
  createSocket?: () => RealtimeSocket | Promise<RealtimeSocket>
  /** Standard: Verbindungs-Store der Hülle */
  onState?: (state: RealtimeState) => void
}

export interface RealtimeBridge {
  /** verbinden (nach der Anmeldung, nach einem Betriebswechsel erneut) */
  start(): Promise<void>
  stop(): void
}

async function defaultSocket(): Promise<RealtimeSocket> {
  const { io } = await import('socket.io-client')
  return io({
    path: REALTIME_PATH,
    withCredentials: true,
    autoConnect: false,
    transports: ['websocket'],
    reconnectionDelay: 1000,
    reconnectionDelayMax: 15_000,
  }) as unknown as RealtimeSocket
}

export function createRealtimeBridge(options: RealtimeBridgeOptions): RealtimeBridge {
  const { queryClient } = options
  const report = options.onState ?? setRealtimeState
  let socket: RealtimeSocket | null = null
  let generation = 0

  const stop = (): void => {
    generation += 1
    socket?.disconnect()
    socket = null
    report('idle')
  }

  return {
    async start() {
      stop()
      const mine = generation
      report('connecting')
      let created: RealtimeSocket
      try {
        created = await (options.createSocket ?? defaultSocket)()
      } catch {
        if (mine === generation) report('unavailable')
        return
      }
      if (mine !== generation) {
        created.disconnect()
        return
      }
      socket = created
      // war die Verbindung schon einmal da? Nur dann ist ein Abriss ein „offline“ – und ein Wiederkommen ein Anlass,
      // alles Sichtbare neu zu laden (in der Lücke können Hinweise verloren gegangen sein).
      let everConnected = false
      let lost = false
      const current = (): boolean => mine === generation

      created.on('connect', () => {
        if (!current()) return
        if (everConnected && lost) void queryClient.invalidateQueries({ queryKey: [ACTION_KEY] })
        everConnected = true
        lost = false
        report('online')
      })
      created.on('disconnect', ((reason: string) => {
        if (!current()) return
        lost = true
        report('offline')
        // socket.io verbindet nach einem Abriss selbst neu – außer der Server hat getrennt
        if (reason === 'io server disconnect') created.connect()
      }) as never)
      created.on('connect_error', ((error: Error | undefined) => {
        if (!current()) return
        // abgelehnt, weil die Sitzung nicht (mehr) gilt: Das merkt die nächste Anfrage ohnehin – kein „offline“
        const rejected = error?.message === 'UNAUTHENTICATED'
        if (everConnected && !rejected) {
          report('offline')
          return
        }
        // nie verbunden: Den Dienst gibt es (noch) nicht – nicht weiter anklopfen
        report('unavailable')
        generation += 1
        created.disconnect()
        socket = null
      }) as never)
      created.on(REALTIME_EVENT, ((payload: unknown) => {
        if (!current()) return
        const hint = parseHint(payload)
        if (!hint) return
        void applyHint(queryClient, hint)
        for (const listener of listeners) listener(hint)
      }) as never)
      created.connect()
    },
    stop,
  }
}
